import { RequireAuth } from '@/components/require-auth'
import { AppLayout } from '@/components/app-layout'
import {
  AgencyService,
  type AgencyMember,
  type AgencyMembersResponse,
  type AgencyPlanInfo,
  type AgencyRole,
} from '@/services/agency-service'
import { getAuthState } from '@/stores/auth-store'
import { ApiError } from '@/lib/api-client'
import {
  Button,
  EmailInput,
  Field,
  NativeSelect,
  Notice,
  type NoticeVariant,
  Option,
  type SelectOption,
} from '@tempots/beatui'
import {
  attr,
  computedOf,
  ForEach,
  html,
  on,
  prop,
  Signal,
  When,
} from '@tempots/dom'
import { Anchor, HTMLTitle, Query } from '@tempots/ui'

const ROLE_LABELS: Record<AgencyRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
}

/**
 * Agency roster: who's in the agency and their roles. Owners manage
 * everyone and can promote members to co-owner (or demote co-owners — the
 * server protects the last owner); admins invite and remove plain members;
 * members see the roster read-only. Everyone can leave, except a sole
 * owner, who must make someone else an owner first. Mirrors the tier
 * rules enforced by the members API.
 */
export function AgencyPage({ agencyId }: { agencyId: Signal<string> }) {
  return RequireAuth({}, () =>
    AppLayout({
      agencyId,
      content: html.div(
        HTMLTitle('Agency - ResumeHeaders'),
        attr.class('p-4 sm:p-8 h-full overflow-auto'),
        Query<
          string,
          {
            members: AgencyMembersResponse
            myRole: AgencyRole | null
            planInfo: AgencyPlanInfo
          }
        >({
          request: agencyId,
          load: async ({ request: id }) => {
            const [members, myRole, planInfo] = await Promise.all([
              AgencyService.listMembers(id),
              AgencyService.getMyAgencyRole(id),
              AgencyService.getPlanInfo(id),
            ])
            return { members, myRole, planInfo }
          },
          keepOnReload: true,
          pending: () =>
            html.div(attr.class('text-gray-500'), 'Loading members…'),
          failure: ({ reload }) =>
            html.div(
              attr.class('flex flex-col gap-3 items-start'),
              Notice(
                { variant: 'danger', title: 'Could not load the roster' },
                'Something went wrong while loading your agency members.'
              ),
              Button({ variant: 'outline', onClick: reload }, 'Try again')
            ),
          success: ({ value, reload }) => {
            const myUserId = getAuthState().user?.id ?? null
            const myRole = value.$.myRole
            const isOwner = myRole.map(role => role === 'owner')

            // Whether the current plan is entitled to a team at all.
            // Reactive twin of the snapshot used for the pitch branch below.
            const hasTeamFeature = value.$.planInfo.map(p =>
              p.availableFeatures.includes('multipleUsers')
            )

            // Solo plans (free/standard/pro) with a one-person roster have
            // no team to manage — the page becomes the pitch for the Agency
            // plan instead of a one-row roster with a disabled invite form.
            // (Plan changes happen on /billing, so a remount on return is
            // fine.) With MORE than one member — e.g. after a trial expired
            // or a subscription lapsed with teammates still aboard — the
            // roster must stay reachable so the owner can remove members
            // (or members can leave); only inviting is entitlement-gated.
            if (
              !value.value.planInfo.availableFeatures.includes(
                'multipleUsers'
              ) &&
              value.value.members.members.length <= 1
            ) {
              return html.div(
                attr.class('flex flex-col gap-5 max-w-2xl mx-auto'),
                html.h1(
                  attr.class('text-xl font-bold text-gray-800'),
                  'Bring your whole team'
                ),
                html.p(
                  attr.class('text-sm text-gray-600'),
                  'The Agency plan turns ResumeHeaders into your firm’s branding hub — every recruiter produces resumes with the same template, and you manage it all from here.'
                ),
                html.ul(
                  attr.class(
                    'list-disc pl-5 text-sm text-gray-700 flex flex-col gap-2'
                  ),
                  html.li(
                    html.b('Invite your recruiters'),
                    ' — everyone works from the same agency workspace.'
                  ),
                  html.li(
                    html.b('One shared template'),
                    ' — change the branding once and every resume follows.'
                  ),
                  html.li(
                    html.b('Owner and admin roles'),
                    ' — control who manages members and the template.'
                  ),
                  html.li(
                    html.b('Everything in Pro included'),
                    ' — unlimited resumes and redaction for the whole team.'
                  )
                ),
                html.div(
                  attr.class('text-sm text-gray-500'),
                  '$19 per user per month — one bill for the whole team, prorated as people join or leave.'
                ),
                When(
                  isOwner,
                  () =>
                    html.div(
                      Anchor(
                        { href: agencyId.map(id => `/${id}/billing`) },
                        attr.class(
                          'inline-block px-4 py-2 rounded-md bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800'
                        ),
                        'Upgrade to Agency'
                      )
                    ),
                  () =>
                    html.p(
                      attr.class('text-sm text-gray-500'),
                      'The agency owner can upgrade under Billing.'
                    )
                )
              )
            }
            const canManage = myRole.map(AgencyService.canManageMembers)
            const atLimit = value.$.planInfo.map(p => p.userLimitReached)
            // Seats are bought under Plans & Billing; invites only fill
            // them, so admins invite too. The cap is the purchased count.
            const paidTeam = value.$.planInfo.map(
              p => p.plan === 'team' && p.complimentary !== true
            )
            // No invites on single-seat plans — the roster is only shown
            // there to manage leftover members (remove/leave).
            const canInvite = computedOf(
              canManage,
              hasTeamFeature
            )((manage, team) => manage && team)

            const busy = prop(false)
            const feedback = prop<{
              kind: 'success' | 'error'
              message: string
            } | null>(null)

            const run = async (action: () => Promise<void>) => {
              busy.set(true)
              feedback.set(null)
              try {
                await action()
                reload()
              } catch (error) {
                feedback.set({
                  kind: 'error',
                  message:
                    error instanceof ApiError && error.message
                      ? error.message
                      : 'The change did not go through. Please try again.',
                })
              } finally {
                busy.set(false)
              }
            }

            // Invite form state. Admins invite members only; owners pick
            // the role (matching the tier rules the server enforces).
            const inviteEmail = prop('')
            const inviteRole = prop<AgencyRole>('member')

            const handleInvite = () => {
              const email = inviteEmail.value.trim()
              if (!email) {
                feedback.set({
                  kind: 'error',
                  message: 'Enter the email address of the person to invite.',
                })
                return
              }
              void run(async () => {
                const member = await AgencyService.addMember(agencyId.value, {
                  email,
                  role: inviteRole.value,
                })
                inviteEmail.set('')
                inviteRole.set('member')
                feedback.set({
                  kind: 'success',
                  message: `${member.email} has been added to the agency and notified by email.`,
                })
              })
            }

            const handleRoleChange = (
              member: AgencyMember,
              role: AgencyRole
            ) => {
              // Ownership moves deserve an explicit confirmation — both
              // granting it and taking it away.
              if (role === 'owner' && member.role !== 'owner') {
                if (
                  !window.confirm(
                    `Make ${member.email} an owner? Owners have full control of the agency, including billing, member management, and agency deletion.`
                  )
                ) {
                  reload()
                  return
                }
              } else if (member.role === 'owner' && role !== 'owner') {
                if (
                  !window.confirm(
                    `Remove owner rights from ${member.email}? They will become ${ROLE_LABELS[role]}.`
                  )
                ) {
                  reload()
                  return
                }
              }
              void run(async () => {
                await AgencyService.updateMemberRole(
                  agencyId.value,
                  member.id,
                  role
                )
                feedback.set({
                  kind: 'success',
                  message: `${member.email} is now ${ROLE_LABELS[role]}.`,
                })
              })
            }

            const handleResendInvite = (member: AgencyMember) =>
              void run(async () => {
                await AgencyService.resendInvite(agencyId.value, member.id)
                feedback.set({
                  kind: 'success',
                  message: `Invitation email re-sent to ${member.email}.`,
                })
              })

            const handleRemove = (member: AgencyMember) => {
              const pending = member.invitePending === true
              const confirmText = pending
                ? `Cancel the invitation for ${member.email}? Their invitation link will stop working.`
                : `Remove ${member.email} from the agency? They will lose access to the agency workspace.`
              if (!window.confirm(confirmText)) {
                return
              }
              void run(async () => {
                await AgencyService.removeMember(agencyId.value, member.id)
                feedback.set({
                  kind: 'success',
                  message: pending
                    ? `The invitation for ${member.email} has been cancelled.`
                    : `${member.email} has been removed from the agency.`,
                })
              })
            }

            const handleLeave = () => {
              if (
                !window.confirm(
                  'Leave this agency? You will lose access to its workspace and template. An admin can invite you back later.'
                )
              ) {
                return
              }
              busy.set(true)
              feedback.set(null)
              void AgencyService.leaveAgency(agencyId.value)
                .then(() => {
                  AgencyService.clearUserAgenciesCache()
                  // Memberships changed — a full reload re-picks the agency
                  window.location.href = '/start'
                })
                .catch(error => {
                  busy.set(false)
                  feedback.set({
                    kind: 'error',
                    message:
                      error instanceof ApiError && error.message
                        ? error.message
                        : 'Leaving the agency did not go through. Please try again.',
                  })
                })
            }

            const checkoutSuccess =
              new URLSearchParams(window.location.search).get('checkout') ===
              'success'
            return html.div(
              attr.class('flex flex-col gap-6 max-w-5xl mx-auto'),
              checkoutSuccess
                ? Notice(
                    { variant: 'success', title: 'Subscription active' },
                    'Your seats are ready — invite your team below.'
                  )
                : null,
              html.div(
                html.h1(
                  attr.class('text-xl font-bold text-gray-800'),
                  'Agency members'
                ),
                html.p(
                  attr.class('text-sm text-gray-600 mt-1'),
                  'Everyone here processes resumes with the agency template. ',
                  'Admins manage members; owners manage admins, billing, and the template.'
                ),
                html.p(
                  attr.class('text-sm font-semibold text-gray-600 mt-1'),
                  value.$.planInfo.map(p => {
                    if (p.complimentary)
                      return `${p.currentUsers} members — complimentary plan`
                    if (p.plan === 'team' && p.maxUsers != null)
                      return `${p.currentUsers} of ${p.maxUsers} seat${p.maxUsers === 1 ? '' : 's'} filled`
                    return p.maxUsers == null
                      ? `${p.currentUsers} seats used`
                      : `${p.currentUsers} of ${p.maxUsers} seats used`
                  })
                )
              ),
              // Over capacity on a single-seat plan (trial expired or a
              // subscription lapsed with teammates aboard): explain the
              // state and the two ways out. Everyone keeps working in the
              // meantime — seat limits only block new invites.
              When(
                hasTeamFeature.map(team => !team),
                () =>
                  Notice(
                    { variant: 'warning', title: 'More members than seats' },
                    When(
                      isOwner,
                      () =>
                        html.span(
                          'Your current plan includes a single seat, so inviting is off. Everyone below can keep working — but to switch to an individual plan, remove the other members first, or upgrade to Agency under ',
                          Anchor(
                            {
                              href: agencyId.map(id => `/${id}/billing`),
                            },
                            attr.class(
                              'text-teal-700 font-semibold hover:underline'
                            ),
                            'Billing'
                          ),
                          ' to keep the whole team.'
                        ),
                      () =>
                        html.span(
                          'The current plan includes a single seat, so inviting is off. Everyone below can keep working; the agency owner can upgrade to Agency under Billing to keep the whole team.'
                        )
                    )
                  )
              ),
              When(
                computedOf(
                  value.$.planInfo,
                  canManage,
                  hasTeamFeature
                )((p, manage, team) => manage && team && p.userLimitReached),
                () =>
                  Notice(
                    { variant: 'warning' },
                    When(
                      isOwner,
                      () =>
                        html.span(
                          'All seats on your plan are in use. Head to ',
                          Anchor(
                            {
                              href: agencyId.map(id => `/${id}/billing`),
                            },
                            attr.class(
                              'text-teal-700 font-semibold hover:underline'
                            ),
                            'Billing'
                          ),
                          ' to add more.'
                        ),
                      () =>
                        html.span(
                          'All seats on your plan are in use. The agency owner can add more under ',
                          Anchor(
                            {
                              href: agencyId.map(id => `/${id}/billing`),
                            },
                            attr.class(
                              'text-teal-700 font-semibold hover:underline'
                            ),
                            'Billing'
                          ),
                          '.'
                        )
                    )
                  )
              ),
              When(canInvite, () =>
                html.form(
                  attr.class('flex flex-wrap items-end gap-3'),
                  on.submit(event => {
                    event.preventDefault()
                    if (atLimit.value) return
                    handleInvite()
                  }),
                  html.div(
                    attr.class('grow max-w-xs'),
                    Field({
                      label: 'Invite by email',
                      content: EmailInput({
                        value: inviteEmail,
                        placeholder: 'colleague@example.com',
                        onInput: v => inviteEmail.set(v),
                        disabled: atLimit,
                      }),
                    })
                  ),
                  When(isOwner, () =>
                    Field({
                      label: 'Role',
                      content: NativeSelect<AgencyRole>({
                        value: inviteRole,
                        onChange: v => inviteRole.set(v),
                        options: [
                          Option.value('member', 'Member'),
                          Option.value('admin', 'Admin'),
                        ] as SelectOption<AgencyRole>[],
                        disabled: atLimit,
                      }),
                    })
                  ),
                  Button(
                    {
                      type: 'submit',
                      variant: 'filled',
                      color: 'primary',
                      loading: busy,
                      disabled: computedOf(
                        busy,
                        atLimit
                      )((b, limit) => b || limit),
                    },
                    'Invite'
                  ),
                  When(paidTeam, () =>
                    html.div(
                      attr.class('basis-full text-xs text-gray-500'),
                      'Invites fill seats you\u2019ve already purchased. Removing a member frees their seat for someone else — the owner buys seats under Plans & Billing.'
                    )
                  )
                )
              ),
              When(
                feedback.map(f => f != null),
                () =>
                  Notice(
                    {
                      variant: feedback.map(
                        (f): NoticeVariant =>
                          f?.kind === 'success' ? 'success' : 'danger'
                      ),
                    },
                    feedback.map(f => f?.message ?? '')
                  )
              ),
              html.div(
                attr.class(
                  'border border-gray-200 rounded-lg overflow-hidden overflow-x-auto'
                ),
                html.table(
                  attr.class('w-full text-sm'),
                  html.thead(
                    html.tr(
                      attr.class(
                        'bg-gray-50 text-left text-gray-600 font-semibold'
                      ),
                      html.th(attr.class('px-4 py-3'), 'Name'),
                      html.th(attr.class('px-4 py-3'), 'Email'),
                      html.th(attr.class('px-4 py-3'), 'Role'),
                      html.th(attr.class('px-4 py-3'), 'Joined'),
                      When(canManage, () =>
                        html.th(attr.class('px-4 py-3'), '')
                      )
                    )
                  ),
                  html.tbody(
                    ForEach(value.$.members.$.members, item => {
                      const isSelf = item.map(m => m.id === myUserId)
                      // Owners set anyone's role — co-owners included; the
                      // server refuses to demote the last remaining owner.
                      const canEditRole = computedOf(
                        isOwner,
                        isSelf
                      )((owner, self) => owner && !self)
                      const canRemove = computedOf(
                        item,
                        myRole,
                        isSelf
                      )((m, actorRole, self) => {
                        if (self) return false
                        // Owners manage everyone, including co-owners
                        // (the server blocks removing the last owner).
                        if (actorRole === 'owner') return true
                        return actorRole === 'admin' && m.role === 'member'
                      })

                      return html.tr(
                        attr.class('border-t border-gray-100'),
                        html.td(
                          attr.class('px-4 py-3 text-gray-800 font-medium'),
                          item.map(m => m.fullName || '—'),
                          When(isSelf, () =>
                            html.span(
                              attr.class('text-gray-400 font-normal'),
                              ' (you)'
                            )
                          ),
                          When(
                            item.map(m => m.invitePending === true),
                            () =>
                              html.span(
                                attr.class(
                                  'ml-2 text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap'
                                ),
                                item.map(m => {
                                  if (!m.inviteExpiresAt)
                                    return 'Invite link expired'
                                  const expires = new Date(m.inviteExpiresAt)
                                  return expires.getTime() <= Date.now()
                                    ? 'Invite link expired'
                                    : `Invite pending · expires ${expires.toLocaleDateString()}`
                                })
                              )
                          )
                        ),
                        html.td(
                          attr.class('px-4 py-3 text-gray-600'),
                          item.map(m => m.email)
                        ),
                        html.td(
                          attr.class('px-4 py-3 min-w-[9rem]'),
                          When(
                            canEditRole,
                            () =>
                              NativeSelect({
                                value: item.map(m => m.role as string),
                                onChange: v =>
                                  handleRoleChange(item.value, v as AgencyRole),
                                options: [
                                  Option.value('member', 'Member'),
                                  Option.value('admin', 'Admin'),
                                  Option.value('owner', 'Owner'),
                                ] as SelectOption<string>[],
                              }),
                            () =>
                              html.span(
                                attr.class('text-gray-700'),
                                item.map(m => ROLE_LABELS[m.role] ?? m.role)
                              )
                          )
                        ),
                        html.td(
                          attr.class('px-4 py-3 text-gray-500'),
                          item.map(m =>
                            new Date(m.joinedAt).toLocaleDateString()
                          )
                        ),
                        When(canManage, () =>
                          html.td(
                            attr.class('px-4 py-3'),
                            html.div(
                              attr.class(
                                'flex flex-row justify-end items-center gap-2 flex-nowrap'
                              ),
                              When(
                                item.map(m => m.invitePending === true),
                                () =>
                                  Button(
                                    {
                                      variant: 'outline',
                                      size: 'sm',
                                      disabled: busy,
                                      onClick: () =>
                                        handleResendInvite(item.value),
                                    },
                                    html.span(
                                      attr.class('whitespace-nowrap'),
                                      'Resend invite'
                                    )
                                  )
                              ),
                              When(canRemove, () =>
                                Button(
                                  {
                                    variant: 'outline',
                                    size: 'sm',
                                    disabled: busy,
                                    onClick: () => handleRemove(item.value),
                                  },
                                  html.span(
                                    attr.class('whitespace-nowrap'),
                                    item.map((m): string =>
                                      m.invitePending === true
                                        ? 'Cancel invite'
                                        : 'Remove'
                                    )
                                  )
                                )
                              )
                            )
                          )
                        )
                      )
                    })
                  )
                )
              ),
              // Anyone may leave on their own — including an owner, as
              // long as another owner remains (the server enforces it and
              // explains what to do otherwise). Deliberately quiet: this
              // is a rare action and must not compete with the roster.
              When(
                myRole.map(role => role != null),
                () =>
                  html.p(
                    attr.class('text-xs text-gray-400 mt-2'),
                    'No longer working with this agency? ',
                    html.button(
                      attr.type('button'),
                      attr.class(
                        'underline hover:text-gray-600 disabled:opacity-50'
                      ),
                      attr.disabled(busy),
                      on.click(handleLeave),
                      'Leave the agency'
                    ),
                    '.'
                  )
              )
            )
          },
        })
      ),
    })
  )
}
